"use client";
import { useEffect, useRef } from "react";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@ui/table";
import { Badge } from "@ui/badge";

const tableData = [
  {
    component: "Attendance",
    percentage: "10",
    description: "Must have minimum 70% attendance",
  },
  {
    component: "Assignment/Presentation",
    percentage: "10",
    description: "At least 1 graded assignment or presentation",
  },
  {
    component: "Test",
    percentage: "20",
    description: "At least 1 graded test",
  },
  {
    component: "Exam Points",
    percentage: "60",
    description: "Multiple Choice Questions (MCQ), Essay Writing, and Oral",
  },
  {
    component: "Paper Resit",
    percentage: "-",
    description: "Takes place within a month from the exam period",
  },
];

export function GradingTable() {
  const tableRef = useRef<HTMLDivElement | null>(null);
  const hasRun = useRef(false);

  useEffect(() => {
    const target = tableRef.current;
    if (!target) return;

    const observer = new IntersectionObserver(
      (entries) => {
        const entry = entries[0];
        if (entry.isIntersecting && !hasRun.current) {
          hasRun.current = true;
          scrollTable();
        }
      },
      { threshold: 0.3 }
    );

    observer.observe(target);
    return () => observer.disconnect();
  }, []);

  const scrollTable = () => {
    const target = tableRef.current;
    if (!target) return;

    const maxScrollLeft = target.scrollWidth - target.clientWidth;
    const scrollStep = 2;
    const intervalDelay = 10;

    const startScroll = setInterval(() => {
      if (target.scrollLeft < maxScrollLeft) {
        target.scrollTo({
          left: target.scrollLeft + scrollStep,
          behavior: "smooth",
        });
      } else {
        clearInterval(startScroll);
        setTimeout(() => {
          target.scrollTo({ left: 0, behavior: "smooth" });
        }, 1000);
      }
    }, intervalDelay);
  };

  return (
    <section id="grade-table" className="mb-16">
      <Card className="border-solid border-2">
        <CardHeader>
          <CardTitle className="text-2xl">Grading System Overview</CardTitle>
          <CardDescription>
            A comprehensive breakdown of our assessment components and
            requirements
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div ref={tableRef} className="overflow-x-auto overflow-y-hidden whitespace-nowrap">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="font-bold">Component</TableHead>
                  <TableHead className="font-bold">Percentage (%)</TableHead>
                  <TableHead className="font-bold">Description</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {tableData.map((row, index) => (
                  <TableRow key={index} className="hover:bg-muted/50">
                    <TableCell className="font-medium">
                      {row.component}
                    </TableCell>
                    <TableCell>
                      {row.percentage !== "-" ? (
                        <Badge variant="secondary">{row.percentage}%</Badge>
                      ) : (
                        <span className="text-muted-foreground">-</span>
                      )}
                    </TableCell>
                    <TableCell className="text-muted-foreground">
                      {row.description}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>
    </section>
  );
}
